// client/src/pages/Contact.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CONTACT_REASONS = [
  'General Inquiry',
  'Technical Support',
  'Pro Subscription / Billing',
  'Knowledge Article Request',
  'Tool / Feature Request',
  'Bug Report',
  'Partnership / API Access',
  'Other',
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', reason: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [focused, setFocused] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('sending');
    try {
      // Opens native email client as fallback — replace with backend endpoint if desired
      const subject = encodeURIComponent(`[BioMedArticles] ${form.reason || 'Contact Form'} — ${form.name}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nReason: ${form.reason}\n\n${form.message}`
      );
      window.location.href = `mailto:support@biomedarticles.com?subject=${subject}&body=${body}`;
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const inputStyle = (name) => ({
    width: '100%',
    padding: '14px 16px',
    background: 'var(--card)',
    border: `1px solid ${focused === name ? 'var(--ink)' : 'var(--line)'}`,
    borderRadius: 0,
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--ink)',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
    appearance: 'none',
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>

      {/* ── HERO ─────────────────────────────────────────────── */}
      <div style={{ background: 'var(--paper)', color: 'var(--ink)', padding: '72px 24px 56px', borderBottom: '2px solid var(--ink)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '3px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 20 }}>
            Contact
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 6vw, 58px)', fontWeight: 400, lineHeight: 1.1, margin: '0 0 20px', color: 'var(--ink)' }}>
            Get in Touch
          </h1>
          <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 560, margin: '0 0 20px' }}>
            Questions about the platform, Pro features, or clinical trial data?
            We respond to every message — usually within one business day.
          </p>
          <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 560, margin: 0, borderLeft: '2px solid var(--ink)', paddingLeft: 16 }}>
            Have a suggestion for a new Knowledge article, a tool you'd like to see, or a feature request?
            Tell us — we review every suggestion and update the platform regularly based on community feedback.
          </p>
        </div>
      </div>

      {/* ── MAIN CONTENT ─────────────────────────────────────── */}
      <div className="contact-layout" style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 24px 100px', display: 'flex', gap: 80, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ── LEFT: CONTACT INFO ─────────────────────────────── */}
        <div className="contact-info" style={{ flex: '0 0 280px', minWidth: 240 }}>

          <div style={{ marginBottom: 48 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '2.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 20 }}>
              Direct Contact
            </div>

            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>Email</div>
              <a href="mailto:support@biomedarticles.com"
                style={{ fontSize: 14, color: 'var(--ink)', textDecoration: 'none', fontWeight: 500, borderBottom: '1px solid var(--line)', paddingBottom: 2 }}
                onMouseEnter={e => e.target.style.borderColor = 'var(--ink)'}
                onMouseLeave={e => e.target.style.borderColor = 'var(--line)'}
              >
                support@biomedarticles.com
              </a>
            </div>

            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>Phone</div>
              <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>540-322-7406</div>
              <div style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: 4 }}>Text only, no calls</div>
            </div>

            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>Address</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
                Public Health Solutions LLC<br />
                2003 Plank Road #1283<br />
                Fredericksburg, VA 22401
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>Response Time</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Within 1 business day</div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 32 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '2.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 16 }}>
              Quick Links
            </div>
            {[
              { to: '/faq', label: 'FAQ — Common Questions' },
              { to: '/knowledge', label: 'Knowledge Center' },
              { to: '/profile', label: 'Manage Subscription' },
            ].map(l => (
              <div key={l.to} style={{ marginBottom: 10 }}>
                <Link to={l.to} style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
                >
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>→</span>
                  {l.label}
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: FORM ────────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 300 }}>

          {status === 'sent' ? (
            <div style={{ textAlign: 'center', padding: '64px 32px', border: '1px solid var(--line-card)', background: 'var(--card)' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 12, color: 'var(--ink)' }}>Message Sent</div>
              <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 32 }}>
                Your email client should have opened with a pre-filled message.<br />
                We'll get back to you within one business day.
              </div>
              <button
                onClick={() => { setStatus(null); setForm({ name: '', email: '', reason: '', message: '' }); }}
                style={{ padding: '12px 28px', background: 'var(--ink)', color: 'var(--paper)', border: 'none', fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer' }}
              >
                SEND ANOTHER
              </button>
            </div>
          ) : (
            <>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '2.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 28 }}>
                Send a Message
              </div>

              <form onSubmit={handleSubmit}>
                {/* Name + Email row */}
                <div className="contact-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 8 }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Dr. Jane Smith"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      onFocus={() => setFocused('name')}
                      onBlur={() => setFocused(null)}
                      style={inputStyle('name')}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 8 }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="you@institution.edu"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      onFocus={() => setFocused('email')}
                      onBlur={() => setFocused(null)}
                      style={inputStyle('email')}
                    />
                  </div>
                </div>

                {/* Reason */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Reason for Contact
                  </label>
                  <select
                    value={form.reason}
                    onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                    onFocus={() => setFocused('reason')}
                    onBlur={() => setFocused(null)}
                    style={{ ...inputStyle('reason'), color: form.reason ? 'var(--ink)' : 'var(--muted-2)' }}
                  >
                    <option value="" disabled>Select a topic…</option>
                    {CONTACT_REASONS.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div style={{ marginBottom: 28 }}>
                  <label style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Message *
                  </label>
                  <textarea
                    required
                    rows={7}
                    placeholder="Describe your question or request in detail…"
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    onFocus={() => setFocused('message')}
                    onBlur={() => setFocused(null)}
                    style={{ ...inputStyle('message'), resize: 'vertical', minHeight: 160, lineHeight: 1.6 }}
                  />
                </div>

                {/* Submit */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <button
                    type="submit"
                    disabled={status === 'sending' || !form.name || !form.email || !form.message}
                    style={{
                      padding: '14px 36px',
                      background: (!form.name || !form.email || !form.message) ? 'var(--line)' : 'var(--ink)',
                      color: (!form.name || !form.email || !form.message) ? 'var(--muted-2)' : 'var(--paper)',
                      border: 'none',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      cursor: (!form.name || !form.email || !form.message) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {status === 'sending' ? 'OPENING…' : 'SEND MESSAGE →'}
                  </button>
                  <div style={{ fontSize: 12, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>
                    * Required fields
                  </div>
                </div>

                {status === 'error' && (
                  <div style={{ marginTop: 16, fontSize: 13, color: 'var(--danger)', padding: '12px 16px', background: 'var(--paper-2)', border: '1px solid var(--danger)' }}>
                    Something went wrong. Please email us directly at support@biomedarticles.com
                  </div>
                )}
              </form>

              {/* Note */}
              <div style={{ marginTop: 32, padding: '16px 20px', background: 'var(--paper-2)', borderLeft: '3px solid var(--ink)' }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>
                  <strong>Pro subscribers:</strong> For urgent billing or account issues, include your account email and we'll prioritize your request.
                {' '}Want a new Knowledge article or tool added? Select <em>"Knowledge Article Request"</em> or <em>"Tool / Feature Request"</em> — we review all suggestions and update the platform regularly.
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── BOTTOM STRIP ─────────────────────────────────────── */}
      <div style={{ background: 'var(--paper-2)', borderTop: '1px solid var(--line)', padding: '40px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 32, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 6, color: 'var(--ink)' }}>About BioMedArticles</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, maxWidth: 460 }}>
              Built by Dr. Nilesh Panchal, MPH, DrPH — with experience at HHS, FDA, DHS, and DoD — BioMedArticles is designed for researchers, clinicians, and patients navigating the clinical trial landscape.
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '2px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 6 }}>Operated by</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>Public Health Solutions LLC</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Fredericksburg, VA</div>
          </div>
        </div>
      </div>
    </div>
  );
}